import type {
  Anchor,
  AppSettings,
  BookMeta,
  Chapter,
  ChapterListItem,
  ChatScope,
  Conversation,
  Highlight,
  Note,
  Position,
  SelectionState,
  SettingsUpdate,
} from '../shared/types';

export type BookDetail = BookMeta & { chapters: ChapterListItem[] };

const TOKEN_KEY = 'margin.token';

/** 401 专用错误：触发全屏令牌遮罩，调用方 catch 后应静默 */
export class AuthError extends Error {
  constructor() {
    super('未授权');
    this.name = 'AuthError';
  }
}

let unauthorizedHandler: (() => void) | null = null;
/** 任何请求收到 401 时回调（main 注册为弹出令牌遮罩） */
export function onUnauthorized(cb: () => void) {
  unauthorizedHandler = cb;
}

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** 附件下载：从 Content-Disposition 解析文件名，blob + a[download] 触发 */
async function downloadResponse(res: Response, fallbackName: string): Promise<void> {
  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new AuthError();
  }
  if (!res.ok) throw new Error(`下载失败（HTTP ${res.status}）`);
  const disposition = res.headers.get('content-disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = star ? decodeURIComponent(star[1]!) : plain ? plain[1]! : fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface BackupStats {
  books: number;
  highlights: number;
  notes: number;
  conversations: number;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? authHeaders() : { ...authHeaders(), 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new AuthError();
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(err?.error ?? `请求失败（HTTP ${res.status}）`);
  }
  return (await res.json()) as T;
}

export const api = {
  listBooks: () => req<BookMeta[]>('GET', '/api/books'),
  importBook: (filename: string, dataBase64: string) =>
    req<BookMeta>('POST', '/api/books/import', { filename, dataBase64 }),
  getBook: (id: string) => req<BookDetail>('GET', `/api/books/${id}`),
  getChapter: (bookId: string, chapterId: string) =>
    req<Chapter>('GET', `/api/books/${bookId}/chapters/${chapterId}`),
  deleteBook: (id: string) => req<{ ok: true }>('DELETE', `/api/books/${id}`),

  getHighlights: (bookId: string) => req<Highlight[]>('GET', `/api/books/${bookId}/highlights`),
  addHighlight: (bookId: string, anchor: Anchor) =>
    req<Highlight>('POST', `/api/books/${bookId}/highlights`, { anchor }),
  deleteHighlight: (bookId: string, id: string) =>
    req<{ ok: true }>('DELETE', `/api/books/${bookId}/highlights/${id}`),

  getNotes: (bookId: string) => req<Note[]>('GET', `/api/books/${bookId}/notes`),
  addNote: (bookId: string, anchor: Anchor | null, text: string) =>
    req<Note>('POST', `/api/books/${bookId}/notes`, { anchor, text }),
  deleteNote: (bookId: string, id: string) => req<{ ok: true }>('DELETE', `/api/books/${bookId}/notes/${id}`),

  getPosition: (bookId: string) => req<Position | null>('GET', `/api/books/${bookId}/position`),
  savePosition: (bookId: string, chapterId: string, blockId: string | null) =>
    req<Position>('PUT', `/api/books/${bookId}/position`, { chapterId, blockId }),

  pushSelection: (state: Omit<SelectionState, 'updatedAt'>) => req<SelectionState>('PUT', '/api/selection', state),
  clearSelection: () => req<null>('PUT', '/api/selection', { clear: true }),

  getChats: (bookId: string, model?: string) =>
    req<Conversation[]>('GET', `/api/books/${bookId}/chats${model ? `?model=${encodeURIComponent(model)}` : ''}`),
  postChat: (bookId: string, message: { model: string; text: string; anchor: Anchor | null; scope: ChatScope }) =>
    req<Conversation>('POST', `/api/books/${bookId}/chats`, message),

  /** 流式对话：逐段回调 delta，服务端事件为 {delta} / {done} / {error} */
  streamChat: async (
    bookId: string,
    message: { model: string; text: string; anchor: Anchor | null; scope: ChatScope },
    callbacks: { onDelta(delta: string): void; onDone(): void; onError(message: string): void },
  ): Promise<void> => {
    const res = await fetch(`/api/books/${bookId}/chat`, {
      method: 'POST',
      headers: { ...authHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(message),
    });
    if (res.status === 401) {
      unauthorizedHandler?.();
      throw new AuthError();
    }
    if (!res.ok || !res.body) {
      const err = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(err?.error ?? `请求失败（HTTP ${res.status}）`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const line = raw.split('\n').find((l) => l.startsWith('data:'));
        if (!line) continue;
        try {
          const event = JSON.parse(line.slice(5).trim()) as { delta?: string; done?: boolean; error?: string };
          if (typeof event.delta === 'string') callbacks.onDelta(event.delta);
          else if (event.done) callbacks.onDone();
          else if (typeof event.error === 'string') callbacks.onError(event.error);
        } catch {
          // 不完整的片段直接忽略
        }
      }
    }
  },

  getSettings: () => req<AppSettings>('GET', '/api/settings'),
  getIntegrations: () => req<{ mcp: { oauthEnabled: boolean; url: string | null } }>('GET', '/api/integrations'),
  saveSettings: (settings: SettingsUpdate) => req<AppSettings>('PUT', '/api/settings', settings),

  /** 导出 Markdown：fetch 带令牌，blob + a[download] 触发下载 */
  exportBook: async (bookId: string, title: string): Promise<void> => {
    const res = await fetch(`/api/books/${bookId}/export`, { headers: authHeaders() });
    await downloadResponse(res, `${title}.md`);
  },

  /** 全量备份下载（settings 已剔除 provider） */
  exportBackup: async (): Promise<void> => {
    const res = await fetch('/api/backup', { headers: authHeaders() });
    await downloadResponse(res, 'margin-backup.json');
  },

  /** 恢复备份：confirm=false 只回预览统计，true 才覆盖写入 */
  restoreBackup: (backup: unknown, confirm: boolean) =>
    req<({ preview: true } | { restored: true }) & BackupStats>('POST', '/api/backup/restore', {
      ...(backup as Record<string, unknown>),
      confirm,
    }),
};
